// Bulk-apply mechanism for AnimatorPanel's bulk-animate flow: given a batch's
// completed items, apply each one via the Phase 2 applyAnimationResult
// decision, using the exact same dispatch wiring the single-image call sites
// use (EditorShortcuts.jsx / EditorApp.jsx / JobWidgetHost.jsx) — createBox +
// addBoxThunk for a box-swap (with the same "plugin missing -> fall back to a
// plain url update" safety net those call sites have), updatePluginToolbar
// for a plain url update. Looped per item — no synthetic-action folding, no
// addBoxThunk re-implementation (product ruling supersedes spec §8: the bulk
// operation is explicitly non-revertable and warned up front, so there is no
// need to collapse N applies into one dispatch for a single undo entry).
//
// Each item carries its own navId (it may live in a different scene than
// whatever is currently open in the editor), so it is passed as
// navItemSelected per-item rather than reading the globally-selected nav item.
//
// These writes must never land in undo history (constraint: bulk apply is
// excluded from undo entirely — per-image revert instead goes through the
// Image Animator's version-history panel). They dispatch the same shared
// ADD_BOX/DELETE_BOX/UPDATE_PLUGIN_TOOLBAR action creators normal single-image
// edits use, which must stay undo-tracked, so a type-based filter (the
// ADD_SOUND precedent, reducers/reducers.jsx:422) isn't an option here — this
// flags window.bulkAnimateApplying for the duration of the loop, the same
// ambient-flag technique reducers.jsx already uses for window.exitFlag.
import IdealStudio from "../../core/editor/main";
import { createBox } from "../../common/common_tools";
import { addBoxThunk, deleteBox, updatePluginToolbar } from "../../common/actions";
import { getDescendantBoxes } from "../../common/utils";
import { applyAnimationResult } from "./applyAnimationResult.es6";

export function applyBulkAnimationResults(results) {
  return (dispatch, getState) => {
    if (!Array.isArray(results) || results.length === 0) return;

    window.bulkAnimateApplying = true;
    try {
      results.forEach((item) => {
        const present = getState().undoGroup.present;
        const decision = applyAnimationResult({
          boxId: item.boxId,
          url: item.resultUrl,
          boxes: present.boxesById,
          pluginToolbars: present.pluginToolbarsById,
          containedViewSelected: null,
          containedViews: present.containedViewsById,
          navItems: present.navItemsById,
          navItemSelected: item.navId,
        });

        if (decision.kind === "boxSwap" && IdealStudio.Plugins.get("EnrichedPlayer")) {
          createBox(
            decision.newBox,
            "EnrichedPlayer",
            decision.inASlide,
            (...args) => dispatch(addBoxThunk(...args)),
            present.boxesById
          );

          const oldBox = present.boxesById[decision.oldBoxId];
          const descendants = getDescendantBoxes(oldBox, present.boxesById);
          let cvs = [...(oldBox.containedViews || [])];
          descendants.forEach((descId) => {
            cvs = [...cvs, ...((present.boxesById[descId] || {}).containedViews || [])];
          });

          dispatch(
            deleteBox(
              decision.oldBoxId,
              decision.parent,
              decision.container,
              descendants,
              cvs,
              decision.page
            )
          );
        } else if (decision.kind !== "noop") {
          dispatch(updatePluginToolbar(item.boxId, "main", "state", "url", item.resultUrl));
        }
      });
    } finally {
      window.bulkAnimateApplying = false;
    }
  };
}
